/**
 * GUC v2 — Loan details.
 *
 * The product is FIXED at "Ground Up Construction": this sizer is the GUC one,
 * so the choice that opens and closes half of the RTL form is not a question
 * here. Everything else is the spec's §2 loan-level set — purpose, term,
 * requested leverage, financed budget %, origination points, pipeline target and
 * the GC completion guaranty.
 */
import { useField } from "react-final-form";

import Box from "@mui/material/Box";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  SZ,
  Section,
  Grid,
  ChoiceRow,
  Field2,
  RffChips,
  RffPurposeChips,
  RffDelayedPurchase,
  RffDate,
  RffMoney,
  RffPercent,
  RffSelect,
  SwitchInput,
} from "../../sizersShared/sizerUi";
import { selectOptionsLoanPurpose } from "../../sizersShared/sizerSelectOptions";
import useSizerCorrespondents from "../../sizersShared/useSizerCorrespondents";
import { gucOptionsPipelineTarget, gucOptionsTermMonths } from "../gucConstants";
import { isPurchasePurpose } from "../gucLoanMath";
import {
  DELAYED_PURCHASE_LABEL,
  canBeDelayedPurchase,
} from "../../../utils/loans/delayedPurchase";
import LeverageOrAmountField from "../../sizersShared/LeverageOrAmountField";

/**
 * The guaranty toggle. Turning it ON where the scenario is blocked for zero
 * ground-up experience is what opens the prefilled exception request — the
 * screen owns that (it needs the priced result and the scenario id), so this
 * only reports the change.
 */
const GcGuarantyField = ({ onTurnedOn }) => {
  const { input } = useField("gcCompletionGuaranty");
  return (
    <Field2
      label="GC completion guaranty"
      tip="A General Contractor completion guaranty is the structuring path for a ground-up deal with no completed ground-up projects. Turning it on opens the exception request."
    >
      <SwitchInput
        value={input.value === true}
        onChange={(next) => {
          input.onChange(next);
          if (next) onTurnedOn?.();
        }}
      />
    </Field2>
  );
};

export default function GucLoanDetailsSection({
  first,
  sizing,
  onGuarantyTurnedOn,
  onClearOverride,
}) {
  const correspondents = useSizerCorrespondents();
  const { input: purpose } = useField("loanPurpose", {
    subscription: { value: true },
  });
  const { input: pipelineTarget } = useField("pipelineTarget", {
    subscription: { value: true },
  });
  const { input: delayedFlag } = useField("delayedPurchase", {
    subscription: { value: true },
  });
  const isPurchase = isPurchasePurpose(purpose.value, delayedFlag.value);
  // `outstandingLoanUpb` is "Refinance Only" on the model, and the closing
  // engine reads it when working out required liquidity.
  const showUpb = Boolean(purpose.value) && !isPurchase;
  const isAcquisition = pipelineTarget.value === "loan_acquisitions";

  return (
    <Section
      first={first}
      title="Loan details"
      subtitle="Ground Up Construction. These choices drive which questions appear below."
    >
      {/* Purpose and term share the first row (7 / 5 — purpose carries the
          long labels); the Delayed purchase? question gets its own row below,
          only on a purchase and required while it shows. `termMonths` moves
          the rate — the sheet carries a `termAdjustments` row per term — so it
          is a priced input, not a label. */}
      <ChoiceRow>
        <Field2 label="Purpose" required>
          <RffPurposeChips sm name="loanPurpose" options={selectOptionsLoanPurpose} required />
        </Field2>
        <Field2 label="Term" required>
          <RffChips sm name="termMonths" options={gucOptionsTermMonths} required />
        </Field2>
        {/* Only on a cash-out refinance (REQ-106), required while shown. */}
        {canBeDelayedPurchase(purpose.value) && (
          <Field2 label={DELAYED_PURCHASE_LABEL} required>
            <RffDelayedPurchase sm />
          </Field2>
        )}
      </ChoiceRow>

      {/* REQUESTED LEVERAGE ON ITS OWN ROW. Every amount on the response derives
          from it, and the backend does that derivation — the strip's live figure
          is a preview of the same arithmetic, never a second answer. */}
      {/* EITHER requested leverage OR an initial loan amount (owner
          2026-10-19). A reprice suggestion writes the amount and flips this
          to "Loan amount $" — the amount it set is right there to read. */}
      <LeverageOrAmountField isPurchase={isPurchase} sizing={sizing} sx={{ mt: "26px" }} />

      <Grid cols={showUpb ? 3 : 2} sx={{ mt: "26px" }}>
        <Field2
          label="Financed budget"
          hint="Share of the construction budget the loan finances. The holdback is this × the budget; the unfinanced remainder lands in required liquidity."
        >
          <RffPercent name="financedBudgetPercent" factor={100} />
        </Field2>
        {/* A fraction on the model (0.02 = 2%) and NETTED from the initial
            advance for the securitization ratios — a priced input, not a fee
            line. */}
        <Field2
          label="Origination points"
          tip="Lender points charged to the borrower. Netted from the initial advance for the takeout ratios."
        >
          <RffPercent name="originationPoints" factor={100} />
        </Field2>
        {showUpb && (
          <Field2
            label="Current loan UPB"
            tip="Refinance only — cash-out proceeds may count toward required liquidity coverage."
          >
            <RffMoney name="outstandingLoanUpb" placeholder="0" />
          </Field2>
        )}
      </Grid>

      <Grid cols={3} sx={{ mt: "26px" }}>
        {/* PIPELINE TARGET, said the way the desk says it. The wire values are
            collection names and never reach the screen. */}
        <Field2
          label="Pipeline target"
          tip="Pre-Close is a deal we are originating. Loan Trade prices an already-closed loan and requires its note date."
        >
          <RffSelect
            name="pipelineTarget"
            options={gucOptionsPipelineTarget}
            placeholder="Pre-Close"
          />
        </Field2>
        <GcGuarantyField onTurnedOn={onGuarantyTurnedOn} />
        <Field2 label="Correspondent" tip="Optional — resolves the account rate spread.">
          <RffSelect
            name="correspondentId"
            options={correspondents}
            placeholder={
              correspondents.length ? "Eastview base (no correspondent)" : "Loading…"
            }
          />
        </Field2>
      </Grid>

      {/* A Loan Trade scenario prices an ALREADY-CLOSED loan, so the note date
          is required — `acquisition_scenario_missing` 422s the price without it,
          the same gate the acquisition model enforces. Asked here rather than
          discovered as a rejected request. */}
      {isAcquisition && (
        <Grid cols={2} sx={{ mt: "26px" }}>
          <Field2
            label="Note origination date"
            required
            hint="Required for a Loan Trade — it drives seasoning and accrual, and boards with the acquisition."
          >
            <RffDate name="originationDate" required />
          </Field2>
        </Grid>
      )}

      <Txt component="div" sx={{ fontSize: 11.5, color: SZ.ink400, mt: "18px" }}>
        Amounts read in build-up order — initial advance, plus construction
        holdback, equals the total loan amount. The total is what the guideline
        minimum and maximum measure.
      </Txt>
    </Section>
  );
}
